import { useState, useEffect } from "react";
import { useNavigate } from "react-router";
import PageMeta from "../../components/common/PageMeta";
import CollaboratorsTable from "../../components/collaborators/CollaboratorsTable";
import api from "../../services/api";
import { authService } from "../../services/authService";

// Interface para os dados de colaboradores
interface CollaboratorData {
    collaborators: Array<any>;
    total: number;
}

export default function MyCollaborators() {
    const navigate = useNavigate();
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [collaboratorsData, setCollaboratorsData] = useState<CollaboratorData | null>(null);
    const [searchTerm, setSearchTerm] = useState("");
    const [statusFilter, setStatusFilter] = useState<string>("all");

    // Buscar dados dos colaboradores
    const fetchCollaborators = async () => {
        setIsLoading(true);
        try {
            const companyId = authService.getCompanyId();
            const response = await api.get(`/api/collaborators?companyId=${companyId}`);

            if (response.data && response.data.status === 'success') {
                setCollaboratorsData(response.data.data);
            }
        } catch (error) {
            console.error("Erro ao buscar colaboradores:", error);
            setCollaboratorsData({ collaborators: [], total: 0 });
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchCollaborators();
    }, []);

    const handleEditClick = (collaborator: any) => {
        navigate(`/collaborators/${collaborator.id}/edit`);
    };

    return (
        <>
            <PageMeta
                title="Sellyo - Colaboradores"
                description="Gerencie os colaboradores da sua empresa"
            />

            <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <h3 className="text-lg font-semibold text-gray-800 dark:text-white/90">
                    Colaboradores
                </h3>

                <button
                    onClick={() => navigate("/collaborators/invite")}
                    className="inline-flex items-center justify-center rounded-lg bg-blue-600 px-4 py-2.5 text-sm font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-4 focus:ring-blue-300 dark:bg-blue-600 dark:hover:bg-blue-700 dark:focus:ring-blue-800"
                >
                    <svg
                        className="mr-2 h-5 w-5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                    >
                        <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M12 6v6m0 0v6m0-6h6m-6 0H6"
                        />
                    </svg>
                    Adicionar colaborador
                </button>
            </div>

            {/* Filtros */}
            <div className="mb-6 rounded-2xl border border-gray-200 bg-white p-5 dark:border-gray-800 dark:bg-white/[0.03]">
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    <div>
                        <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                            Buscar
                        </label>
                        <input
                            type="text"
                            placeholder="Nome ou e-mail..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="w-full rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm text-gray-900 focus:border-blue-500 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white dark:placeholder-gray-400"
                        />
                    </div>

                    <div>
                        <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                            Status
                        </label>
                        <select
                            value={statusFilter}
                            onChange={(e) => setStatusFilter(e.target.value)}
                            className="w-full rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm text-gray-900 focus:border-blue-500 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                        >
                            <option value="all">Todos</option>
                            <option value="active">Ativos</option>
                            <option value="pending">Pendentes</option>
                            <option value="inactive">Inativos</option>
                        </select>
                    </div>
                </div>
            </div>

            {/* Tabela de Colaboradores */}
            <CollaboratorsTable
                collaborators={collaboratorsData?.collaborators || []}
                isLoading={isLoading}
                searchTerm={searchTerm}
                statusFilter={statusFilter}
                onEditClick={handleEditClick}
                onRefresh={fetchCollaborators}
            />

        </>
    );
}
